"use client";

import { useState, type CSSProperties, type ReactNode } from "react";
import { BookMarked, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import CourseOutline from "@/components/CourseOutline";
import NoteDrawer from "@/components/NoteDrawer";
import { cn } from "@/lib/utils";
import type { Catalog } from "@/lib/api";

/**
 * 通用课程布局。
 *
 * - 常规（default）：文档流，左右侧栏 sticky。
 * - 全屏（full）：左右侧栏 position:fixed 锁定屏幕两侧（不随滚动移动），
 *   中间内容留边自己滚动 —— 彻底解决"侧栏跟着页面动"（不依赖 vh 估算）。
 *
 * 左侧大纲栏可折叠（右缘按钮 / 折叠后左缘竖条展开）。
 * 右栏宽度由调用方控制（rightWidth 传入，内容 pr 同步）。
 * 右下角提供"书本侧边笔记"抽屉入口（notePageId 存在时显示）。
 */
export default function CourseLayout({
  catalog,
  courseId,
  currentConceptId,
  activeChapterId,
  notePageId,
  noteConceptId,
  noteSectionId,
  noteChapterId,
  noteTitle,
  noteButtonClassName,
  noteButtonOffset,
  right,
  full = false,
  rightWidth = 360,
  contentWidth,
  children,
}: {
  catalog: Catalog;
  courseId: string;
  currentConceptId?: string;
  activeChapterId?: string;
  notePageId?: string;
  noteConceptId?: string;
  noteSectionId?: string;
  noteChapterId?: string;
  noteTitle?: string;
  noteButtonClassName?: string;
  noteButtonOffset?: number;
  right?: ReactNode;
  full?: boolean;
  contentWidth?: number;
  rightWidth?: number;
  children: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [outlineCollapsed, setOutlineCollapsed] = useState(false);

  // 折叠后左缘的展开竖条（两种布局共用）
  const expandOutlineButton = outlineCollapsed ? (
    <button
      onClick={() => setOutlineCollapsed(false)}
      className="fixed left-0 top-1/2 z-40 hidden -translate-y-1/2 flex-col items-center gap-1 rounded-r-xl border border-l-0 border-primary/30 bg-background px-2 py-3 text-xs text-muted-foreground shadow-md transition-colors hover:text-primary lg:flex"
      title="展开大纲"
      aria-label="展开大纲"
    >
      <ChevronRight className="h-4 w-4" />
      <span className="[writing-mode:vertical-rl]">大纲</span>
    </button>
  ) : null;

  if (full) {
    return (
      <>
        {/* 移动端：大纲折叠入口 */}
        <div className="mb-3 lg:hidden">
          <button
            onClick={() => setMobileOpen((v) => !v)}
            aria-expanded={mobileOpen}
            aria-controls="course-outline-mobile"
            className="flex w-full items-center justify-between rounded-lg bg-muted/70 px-3.5 py-2.5 text-sm font-medium text-foreground transition-colors active:bg-muted"
          >
            <span className="flex items-center gap-1.5">
              <BookMarked className="h-4 w-4 text-primary" />
              课程目录
            </span>
            <ChevronDown
              className={cn("h-4 w-4 text-muted-foreground transition-transform", mobileOpen && "rotate-180")}
            />
          </button>
          {mobileOpen && (
            <div id="course-outline-mobile" className="mt-2 overflow-hidden rounded-xl border bg-background p-2 shadow-sm">
              <CourseOutline
                catalog={catalog}
                courseId={courseId}
                currentConceptId={currentConceptId}
                activeChapterId={activeChapterId}
              />
            </div>
          )}
        </div>

        {/*
          全屏三栏：侧栏 fixed 锁定屏幕两侧（不随滚动移动），
          内容区始终渲染一次，桌面用 margin 留边、移动端正常流。
        */}
        <div>
          {/* fixed 左栏（仅桌面，可折叠） */}
          {!outlineCollapsed && (
            <aside className="fixed bottom-0 left-0 top-[var(--app-header-height)] z-30 hidden w-[280px] border-r bg-background lg:block">
              <div className="flex h-full flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto p-3">
                  <CourseOutline
                    catalog={catalog}
                    courseId={courseId}
                    currentConceptId={currentConceptId}
                    activeChapterId={activeChapterId}
                  />
                </div>
              </div>

              {/* 折叠按钮：右缘中间 */}
              <button
                onClick={() => setOutlineCollapsed(true)}
                className="absolute inset-y-0 -right-px z-10 flex w-5 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                title="折叠大纲"
                aria-label="折叠大纲"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            </aside>
          )}

          {expandOutlineButton}

          {/* fixed 右栏（仅宽桌面，可调宽）；xl 以下保持 SSR 与首屏布局稳定 */}
          {right && (
            <aside
              className="fixed bottom-0 right-0 top-[var(--app-header-height)] z-30 hidden border-l bg-background xl:block"
              style={{ width: rightWidth }}
            >
              <div className="flex h-full flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto p-3">{right}</div>
              </div>
            </aside>
          )}

          {/* 内容区：children 仅渲染一次，宽桌面 margin 避让 fixed 右栏 */}
          <div
            className="min-h-screen px-4 pt-6 pb-[calc(env(safe-area-inset-bottom)+4rem)] md:pb-6"
            style={{ "--right-w": `${right ? rightWidth : 0}px` } as CSSProperties}
          >
            <div
              className={cn(
                outlineCollapsed ? "lg:ml-0" : "lg:ml-[280px]",
                right ? "xl:mr-[var(--right-w)]" : ""
              )}
            >
              {/* 阅读宽度约束：内容列可拖拽调宽（contentWidth 传入）；未传时回退 max-w-3xl */}
              <div
                className="mx-auto w-full max-w-3xl"
                style={contentWidth ? { maxWidth: `${contentWidth}px` } : undefined}
              >
                {children}
              </div>
            </div>
          </div>
        </div>

        {notePageId && (
          <NoteDrawer
            pageId={notePageId}
            conceptId={noteConceptId}
            sectionId={noteSectionId}
            chapterId={noteChapterId || activeChapterId}
            title={noteTitle}
            buttonClassName={noteButtonClassName}
            buttonOffsetRight={noteButtonOffset}
          />
        )}
      </>
    );
  }

  // 常规两栏/三栏（sticky）
  return (
    <>
      {/* 移动端：大纲折叠入口 */}
      <div className="mb-3 lg:hidden">
        <button
          onClick={() => setMobileOpen((v) => !v)}
          aria-expanded={mobileOpen}
          aria-controls="course-outline-mobile"
          className="flex w-full items-center justify-between rounded-lg bg-muted/70 px-3.5 py-2.5 text-sm font-medium text-foreground transition-colors active:bg-muted"
        >
          <span className="flex items-center gap-1.5">
            <BookMarked className="h-4 w-4 text-primary" />
            课程目录
          </span>
          <ChevronDown
            className={cn("h-4 w-4 text-muted-foreground transition-transform", mobileOpen && "rotate-180")}
          />
        </button>
        {mobileOpen && (
          <div id="course-outline-mobile" className="mt-2 overflow-hidden rounded-xl border bg-background p-2 shadow-sm">
            <CourseOutline
              catalog={catalog}
              courseId={courseId}
              currentConceptId={currentConceptId}
              activeChapterId={activeChapterId}
            />
          </div>
        )}
      </div>

      <div
        className={
          right
            ? outlineCollapsed
              ? "lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-5"
              : "lg:grid lg:grid-cols-[280px_minmax(0,1fr)_360px] lg:gap-5 xl:grid-cols-[300px_minmax(0,1fr)_360px]"
            : outlineCollapsed
              ? "lg:grid lg:grid-cols-[minmax(0,1fr)]"
              : "lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-6 xl:grid-cols-[300px_minmax(0,1fr)]"
        }
      >
        {!outlineCollapsed && (
          <aside className="hidden self-start lg:block">
            <div className="sticky top-[calc(var(--app-header-height)+1rem)] flex h-[calc(100vh-var(--app-header-height)-2rem)] w-full flex-col overflow-hidden rounded-xl border bg-background p-3 shadow-sm">
              <div className="flex-1 overflow-y-auto">
                <CourseOutline
                  catalog={catalog}
                  courseId={courseId}
                  currentConceptId={currentConceptId}
                  activeChapterId={activeChapterId}
                />
              </div>
              <button
                onClick={() => setOutlineCollapsed(true)}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                title="折叠大纲"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                折叠大纲
              </button>
            </div>
          </aside>
        )}

        <div className="min-w-0">
          {children}
        </div>

        {right && (
          <div className="hidden min-w-0 lg:block">
            <div className="sticky top-[calc(var(--app-header-height)+1rem)] w-[360px]">{right}</div>
          </div>
        )}
      </div>

      {expandOutlineButton}

      {notePageId && (
        <NoteDrawer
          pageId={notePageId}
          conceptId={noteConceptId}
          sectionId={noteSectionId}
          chapterId={noteChapterId || activeChapterId}
          title={noteTitle}
          buttonClassName={noteButtonClassName}
          buttonOffsetRight={noteButtonOffset}
        />
      )}
    </>
  );
}
